import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"

export type Car = {
  id: string
  legacyId: string
  label: string
  plates: string
  // gps sets "long_ago" once the provider stopped listing the device for
  // over an hour. A dead box the provider still lists stays "recently".
  lastSeen?: CarLastSeen
  // The GPS provider's brand ("Webfleet", "Holte"), stamped by gps from the
  // integration that listed the car. Missing on hand-added cars and on cars
  // gps hasn't stamped yet.
  gpsBrand?: string
}

export type CarLastSeen = "recently" | "long_ago"

type CarsState = Record<string, Car>

type CarsAction = { cars: Car[] }
type AddCarAction = { car: Car }
type RemoveCarAction = { id: string }

const initialState: CarsState = {}

const carsSlice = createSlice({
  name: "cars",
  initialState,
  reducers: {
    setAllCars: (_state, action: PayloadAction<CarsAction>) => {
      const next: CarsState = {}
      action.payload.cars.forEach((car) => {
        next[car.id] = car
      })
      return next
    },

    addCar: (state, action: PayloadAction<AddCarAction>) => {
      state[action.payload.car.id] = action.payload.car
    },
    removeCar: (state, action: PayloadAction<RemoveCarAction>) => {
      delete state[action.payload.id]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const { setAllCars, addCar, removeCar } = carsSlice.actions

export const selectCarById =
  (carId: string | undefined) =>
  (state: { cars: CarsState }): Car | undefined =>
    carId ? state.cars[carId] : undefined

export default carsSlice.reducer
